/**
 * This component will render fullbleed feature content
 *
 * @module views/components/FullBleedContentFeature
 * @memberof -Common
 */
import './FullBleedContentFeature.scss';

import React from 'react';

import classNames from 'classnames';
import PropTypes from 'prop-types';

import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';
import UltaAsset from '@ulta/core/components/UltaAsset/UltaAsset';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { spreadProps } from '@ulta/core/utils/dom/dom';

import Tag from '@ulta/modules/Tag/Tag';

import ActionGroup from '../ActionGroup/ActionGroup';

/**
 * Represents a FullBleedContentFeature component
 *
 * @method
 * @param {FullBleedContentFeatureProps} props - React properties passed from composition
 * @returns FullBleedContentFeature
 */
export const FullBleedContentFeature = function( props ){
  const { inflection } = useDeviceInflection();
  const {
    actionGroup,
    appDownloadLabel,
    androidAppImage,
    androidAppLink,
    backgroundColor,
    bulletPoints,
    description,
    eyebrow,
    iconImage,
    image,
    iosAppImage,
    iosAppLink,
    mirror,
    showAppDownload,
    tag,
    title = defaultProps.title
  } = props;

  const isMobile = inflection.MOBILE;
  const showAppDesktop = ( iconImage && appDownloadLabel && showAppDownload && !isMobile );
  const showAppMobile = ( iosAppImage && androidAppImage && showAppDownload && isMobile );
  const showActionGroup = ( ( !!actionGroup?.action1.label || !!actionGroup?.action2.label ) && !showAppDownload );

  return (
    <div className={ classNames( 'FullBleedContentFeature', {
      [`FullBleedContentFeature__backgroundColor--${backgroundColor}`]: backgroundColor
    } )
    }
    >
      <div className='FullBleedContentFeature__wrapper'>
        <div className={ classNames( 'FullBleedContentFeature__content', {
          'FullBleedContentFeature__content--showButton' : !showAppDownload,
          'FullBleedContentFeature__content--mirror': mirror
        } )
        }
        >
          <div className='FullBleedContentFeature__textContent'>
            { tag &&
              <div className='FullBleedContentFeature__contentTag'>
                <Tag
                  style='default'
                  message={ tag }
                />
              </div>
            }
            { eyebrow &&
              <div className='FullBleedContentFeature__contentEyebrow'>
                <Text
                  htmlTag='p'
                  textStyle='eyebrow'
                  textAlign='left'
                >
                  { eyebrow }
                </Text>
              </div>
            }
            { title && title.text &&
              <div className='FullBleedContentFeature__headingTitle'>
                <Text
                  htmlTag={ title.htmlTag }
                  textStyle={ title.textStyle }
                  color={ title.color }
                  textAlign={ title.textAlign }
                >
                  { title.text }
                </Text>
              </div>
            }
            { description &&
              <div className='FullBleedContentFeature__description'>
                <Text
                  htmlTag='p'
                  textStyle='subtitle-1'
                  textAlign='left'
                >
                  { description }
                </Text>
              </div>
            }
            { bulletPoints &&
              <ul className='FullBleedContentFeature__features'>
                { bulletPoints.map( ( feature, index ) => (
                  <li key={ index }>
                    <Text
                      textStyle='subtitle-1'
                      htmlTag='span'
                      textAlign='center'
                    >
                      { feature }
                    </Text>
                  </li>
                ) ) }
              </ul>
            }
            { showAppDesktop &&
              <div className='FullBleedContentFeature__appDownload'>
                <div className='FullBleedContentFeature__appDownloadImage'>
                  <UltaAsset
                    { ...spreadProps( { ...iconImage } ) }
                    height={ 90 }
                    width={ 90 }
                  />
                </div>
                <div className='FullBleedContentFeature__appDownloadText'>
                  <Text
                    htmlTag='p'
                    textStyle='body-2'
                    textAlign='left'
                  >
                    { appDownloadLabel }
                  </Text>
                </div>
              </div>
            }
            { showActionGroup &&
            <div className='FullBleedContentFeature__actions'>
              <ActionGroup
                action1={ actionGroup.action1 }
                action2={ actionGroup.action2 }
                action1Style={ actionGroup.action1Style }
                action2Style={ actionGroup.action2Style }
              />
            </div>
            }
          </div>
          <div className='FullBleedContentFeature__imageContent'>
            <UltaAsset
              { ...spreadProps( { ...image } ) }
            />
          </div>
          { showAppMobile &&
            <div className='FullBleedContentFeature__appContent'>
              <div className='FullBleedContentFeature__appStore'>
                <div className='FullBleedContentFeature__appStoreIos'>
                  <Link_Huge
                    url={ iosAppLink }
                  >
                    <UltaAsset
                      { ...spreadProps( { ...iosAppImage } ) }
                      width={ 126 }
                    />
                  </Link_Huge>
                </div>
                <div className='FullBleedContentFeature__appStoreAndroid'>
                  <Link_Huge
                    url={ androidAppLink }
                  >
                    <UltaAsset
                      { ...spreadProps( { ...androidAppImage } ) }
                      width={ 138 }
                    />

                  </Link_Huge>
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    </div>
  );
};

/**
 * Property type definitions
 * @typedef FullBleedContentFeatureProps
 * @type {object}
 * @property {string} tag - Sets the tag
 * @property {string} eyebrow - Sets the eyebrow
 * @property {object} title - Sets the title properties
 * @property {string} description - Sets the description
 * @property {array} bulletPoints - Sets the bulletPoints
 * @property {string} backgroundColor - Sets the backgroundColor
 * @property {object} image - Sets the image url
 * @property {boolean} mirror - Sets the mirror
 * @property {object} iosAppImage - Sets the iosAppImage url
 * @property {object} androidAppImage - Sets the androidAppImage url
 * @property {object} iconImage - Sets the iconImage url
 * @property {boolean} appDownloadLabel - Sets the appDownloadLabel
 * @property {string} actionGroup - Set the action1 and/or action2
 * @property {string} iosAppLink - iOSApp download link
 * @property {string} androidAppLink - androidApp download link
 */
export const propTypes =  {
  tag: PropTypes.string,
  eyebrow: PropTypes.string,
  title: PropTypes.object,
  description: PropTypes.string,
  bulletPoints: PropTypes.array,
  backgroundColor: PropTypes.string,
  image: PropTypes.object,
  mirror: PropTypes.bool,
  iosAppImage: PropTypes.object,
  androidAppImage: PropTypes.object,
  iconImage: PropTypes.object,
  appDownloadLabel: PropTypes.string,
  showAppDownload: PropTypes.bool,
  actionGroup: PropTypes.object,
  iosAppLink: PropTypes.string,
  androidAppLink: PropTypes.string
};

/**
* Default values for passed properties
* @type {object}
* @property {object} title - Sets the title properties
* @property {string} title.textStyle - Sets the title textStyle
*/
export const defaultProps = {
  title: {
    textStyle: 'title-4'
  }
};

FullBleedContentFeature.defaultProps = defaultProps;
FullBleedContentFeature.propTypes = propTypes;

export default FullBleedContentFeature;
